import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Paper } from "@mui/material";
import { fetchDriveItemContent } from "../../services/drive.service";

interface RawTextViewerProps {
  item: any;
}

export default function RawTextViewer({ item }: RawTextViewerProps) {
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      setError("");
      try {
        if (item.fileUrl) {
          const response = await fetch(item.fileUrl);
          if (!response.ok) throw new Error("Failed to fetch file content");

          const text = await response.text();

          // If it's a JSON file, pretty-print it for easier reading
          if (item.name?.toLowerCase().endsWith(".json") || item.file?.mimeType?.includes("json")) {
            try {
              const parsed = JSON.parse(text);
              setContent(JSON.stringify(parsed, null, 2));
            } catch (e) {
              setContent(text);
            }
          } else {
            setContent(text);
          }
        } else if (item.data) {
          // Inline data already attached (e.g. FactFind from client-bundle endpoint)
          setContent(JSON.stringify(item.data, null, 2));
        } else if (item._id) {
          // DB-backed DriveItem — fetch content via the content endpoint (same as TranscriptViewer)
          const res: any = await fetchDriveItemContent(item._id);
          if (res && !res.hasError && res.content) {
            setContent(JSON.stringify(res.content, null, 2));
          } else {
            throw new Error("Failed to fetch file content from database.");
          }
        } else {
          setError("No file URL available to preview this text.");
        }
      } catch (err: any) {
        console.error("Raw text load error:", err);
        setError(err.message || "Failed to load file data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (item) fetchContent();
  }, [item]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: "#1e1e1e", p: 3, boxSizing: "border-box" }}>
      <Paper 
        elevation={0} 
        sx={{ 
          maxWidth: "1000px", 
          margin: "0 auto", 
          p: 3, 
          bgcolor: "transparent",
          color: "#d4d4d4", // Light grey text for dark mode code viewer
        }}
      >
        <pre style={{ 
            whiteSpace: "pre-wrap", 
            wordBreak: "break-word", 
            fontFamily: "monospace",
            fontSize: "14px",
            margin: 0
        }}>
          {content}
        </pre>
      </Paper>
    </Box>
  );
}